'use client';

import {
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@commenda/kakashi';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import type { ComponentVisibility, ValidationRules } from '@commenda/types';

// Inline "Only when" condition editor for a single validation rule
function RuleWhenEditor({
  condition,
  availableFields,
  onChange,
}: {
  condition?: ComponentVisibility['condition'];
  availableFields: { fieldId: string; label: string }[];
  onChange: (condition: ComponentVisibility['condition'] | undefined) => void;
}) {
  const field = condition?.field ?? '';
  const operator = (condition?.operator as string) || 'equals';
  const value = condition?.value ?? '';

  if (availableFields.length === 0) return null;

  return (
    <div className="space-y-2 pt-2 border-t border-border-secondary">
      <Label className="text-text-tertiary txt-xs-regular">Only when</Label>
      <div className="flex gap-2 flex-wrap items-center">
        <Select
          value={field || '__none__'}
          onValueChange={(v) =>
            onChange(
              v === '__none__'
                ? undefined
                : {
                    ...condition,
                    field: v,
                    operator:
                      operator as ComponentVisibility['condition']['operator'],
                    value,
                  }
            )
          }
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Field" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">No condition</SelectItem>
            {availableFields.map((f) => (
              <SelectItem key={f.fieldId} value={f.fieldId}>
                {f.label || f.fieldId}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {field && (
          <>
            <Select
              value={operator}
              onValueChange={(v) =>
                onChange({
                  ...condition,
                  field,
                  operator: v as ComponentVisibility['condition']['operator'],
                  value,
                })
              }
            >
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="equals">equals</SelectItem>
                <SelectItem value="notEquals">not equals</SelectItem>
                <SelectItem value="contains">contains</SelectItem>
                <SelectItem value="greaterThan">greater than</SelectItem>
                <SelectItem value="lessThan">less than</SelectItem>
              </SelectContent>
            </Select>
            <Input
              className="w-24"
              value={String(value)}
              onChange={(e) =>
                onChange({
                  ...condition,
                  field,
                  operator:
                    operator as ComponentVisibility['condition']['operator'],
                  value: e.target.value,
                })
              }
              placeholder="Value"
            />
          </>
        )}
      </div>
    </div>
  );
}

export function ValidationEditor({
  validation,
  componentType,
  onChange,
  availableFields,
  currentFieldId,
  isInRepeatableModule,
}: {
  validation?: { rules?: ValidationRules; triggers?: string[] };
  componentType: string;
  onChange: (
    validation: { rules?: ValidationRules; triggers?: string[] } | undefined
  ) => void;
  availableFields?: { fieldId: string; label: string }[];
  currentFieldId?: string;
  isInRepeatableModule?: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(!!validation?.rules);
  const rules = validation?.rules || {};
  const fieldsForWhen = availableFields || [];

  // Filter out the current field from available fields for "Not Same As" validation
  const otherFields = (availableFields || []).filter(
    (f) => f.fieldId !== currentFieldId
  );

  const updateRule = (
    ruleName: keyof ValidationRules,
    value: ValidationRules[keyof ValidationRules] | undefined
  ) => {
    const newRules = { ...rules };
    if (value === undefined) {
      delete newRules[ruleName];
    } else {
      (newRules as Record<string, unknown>)[ruleName] = value;
    }

    if (Object.keys(newRules).length === 0) {
      onChange(undefined);
    } else {
      onChange({ ...validation, rules: newRules });
    }
  };

  const isRequired =
    typeof rules.required === 'object'
      ? rules.required.enabled
      : !!rules.required;

  const showTextValidation = [
    'Input',
    'TextArea',
    'PasswordInput',
    'PhoneInput',
    'UrlInput',
  ].includes(componentType);
  const showNumberValidation = ['NumberInput'].includes(componentType);
  const showEmailValidation = componentType === 'Input';
  const showPhoneValidation = ['PhoneInput'].includes(componentType);
  const showUrlValidation = ['UrlInput'].includes(componentType);
  const showPatternValidation = ['Input', 'TextArea'].includes(componentType);

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2"
      >
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-text-tertiary" />
        ) : (
          <ChevronRight className="w-4 h-4 text-text-tertiary" />
        )}
        <h3 className="txt-small-medium text-text-secondary">
          Validation Rules
        </h3>
      </button>

      {isExpanded && (
        <div className="space-y-4 pl-6 border-l-2 border-border-secondary">
          {/* Required */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Required</Label>
              <Switch
                checked={isRequired}
                onCheckedChange={(checked) =>
                  updateRule(
                    'required',
                    checked
                      ? { enabled: true, message: 'This field is required' }
                      : undefined
                  )
                }
              />
            </div>
            {isRequired && (
              <>
                <Input
                  value={
                    typeof rules.required === 'object'
                      ? rules.required.message || ''
                      : 'This field is required'
                  }
                  onChange={(e) =>
                    updateRule('required', {
                      enabled: true,
                      message: e.target.value,
                      when:
                        typeof rules.required === 'object'
                          ? rules.required.when
                          : undefined,
                    })
                  }
                  placeholder="Error message"
                />
                {typeof rules.required === 'object' && (
                  <RuleWhenEditor
                    condition={rules.required.when}
                    availableFields={fieldsForWhen}
                    onChange={(when) =>
                      updateRule('required', {
                        ...(rules.required as Record<string, unknown>),
                        when,
                      })
                    }
                  />
                )}
              </>
            )}
          </div>

          {/* Text Length Validation */}
          {showTextValidation && (
            <>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Min Length</Label>
                  <Switch
                    checked={!!rules.minLength}
                    onCheckedChange={(checked) =>
                      updateRule(
                        'minLength',
                        checked ? { value: 1, message: 'Too short' } : undefined
                      )
                    }
                  />
                </div>
                {rules.minLength && (
                  <>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        value={String(rules.minLength.value ?? '')}
                        onChange={(e) =>
                          updateRule('minLength', {
                            ...rules.minLength,
                            value: parseInt(e.target.value, 10) || 0,
                          })
                        }
                        placeholder="Min characters"
                        className="w-24"
                      />
                      <Input
                        value={rules.minLength.message || ''}
                        onChange={(e) =>
                          updateRule('minLength', {
                            ...rules.minLength,
                            message: e.target.value,
                          })
                        }
                        placeholder="Error message"
                        className="flex-1"
                      />
                    </div>
                    <RuleWhenEditor
                      condition={rules.minLength.when}
                      availableFields={fieldsForWhen}
                      onChange={(when) =>
                        updateRule('minLength', { ...rules.minLength, when })
                      }
                    />
                  </>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Max Length</Label>
                  <Switch
                    checked={!!rules.maxLength}
                    onCheckedChange={(checked) =>
                      updateRule(
                        'maxLength',
                        checked
                          ? { value: 100, message: 'Too long' }
                          : undefined
                      )
                    }
                  />
                </div>
                {rules.maxLength && (
                  <>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        value={String(rules.maxLength.value ?? '')}
                        onChange={(e) =>
                          updateRule('maxLength', {
                            ...rules.maxLength,
                            value: parseInt(e.target.value, 10) || 0,
                          })
                        }
                        placeholder="Max characters"
                        className="w-24"
                      />
                      <Input
                        value={rules.maxLength.message || ''}
                        onChange={(e) =>
                          updateRule('maxLength', {
                            ...rules.maxLength,
                            message: e.target.value,
                          })
                        }
                        placeholder="Error message"
                        className="flex-1"
                      />
                    </div>
                    <RuleWhenEditor
                      condition={rules.maxLength.when}
                      availableFields={fieldsForWhen}
                      onChange={(when) =>
                        updateRule('maxLength', { ...rules.maxLength, when })
                      }
                    />
                  </>
                )}
              </div>
            </>
          )}

          {/* Number Validation */}
          {showNumberValidation && (
            <>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Minimum Value</Label>
                  <Switch
                    checked={!!rules.min}
                    onCheckedChange={(checked) =>
                      updateRule(
                        'min',
                        checked
                          ? { value: 0, message: 'Value too low' }
                          : undefined
                      )
                    }
                  />
                </div>
                {rules.min && (
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      value={String(rules.min.value ?? '')}
                      onChange={(e) =>
                        updateRule('min', {
                          ...rules.min,
                          value: parseFloat(e.target.value) || 0,
                        })
                      }
                      placeholder="Min value"
                      className="w-24"
                    />
                    <Input
                      value={rules.min.message || ''}
                      onChange={(e) =>
                        updateRule('min', {
                          ...rules.min,
                          message: e.target.value,
                        })
                      }
                      placeholder="Error message"
                      className="flex-1"
                    />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Maximum Value</Label>
                  <Switch
                    checked={!!rules.max}
                    onCheckedChange={(checked) =>
                      updateRule(
                        'max',
                        checked
                          ? { value: 100, message: 'Value too high' }
                          : undefined
                      )
                    }
                  />
                </div>
                {rules.max && (
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      value={String(rules.max.value ?? '')}
                      onChange={(e) =>
                        updateRule('max', {
                          ...rules.max,
                          value: parseFloat(e.target.value) || 0,
                        })
                      }
                      placeholder="Max value"
                      className="w-24"
                    />
                    <Input
                      value={rules.max.message || ''}
                      onChange={(e) =>
                        updateRule('max', {
                          ...rules.max,
                          message: e.target.value,
                        })
                      }
                      placeholder="Error message"
                      className="flex-1"
                    />
                  </div>
                )}
              </div>
            </>
          )}

          {/* Email Validation */}
          {showEmailValidation && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Email Format</Label>
                <Switch
                  checked={!!rules.email}
                  onCheckedChange={(checked) =>
                    updateRule(
                      'email',
                      checked
                        ? {
                            enabled: true,
                            message: 'Please enter a valid email',
                          }
                        : undefined
                    )
                  }
                />
              </div>
              {rules.email && typeof rules.email === 'object' && (
                <Input
                  value={rules.email.message || ''}
                  onChange={(e) =>
                    updateRule('email', {
                      enabled: true,
                      message: e.target.value,
                    })
                  }
                  placeholder="Error message"
                />
              )}
            </div>
          )}

          {/* Phone Validation */}
          {showPhoneValidation && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Phone Format</Label>
                <Switch
                  checked={!!rules.phone}
                  onCheckedChange={(checked) =>
                    updateRule(
                      'phone',
                      checked
                        ? {
                            enabled: true,
                            message: 'Please enter a valid phone number',
                          }
                        : undefined
                    )
                  }
                />
              </div>
              {rules.phone && typeof rules.phone === 'object' && (
                <Input
                  value={rules.phone.message || ''}
                  onChange={(e) =>
                    updateRule('phone', {
                      enabled: true,
                      message: e.target.value,
                    })
                  }
                  placeholder="Error message"
                />
              )}
            </div>
          )}

          {/* URL Validation */}
          {showUrlValidation && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>URL/Domain Format</Label>
                <Switch
                  checked={!!rules.url}
                  onCheckedChange={(checked) =>
                    updateRule(
                      'url',
                      checked
                        ? {
                            enabled: true,
                            message: 'Please enter a valid HTTPS URL or domain',
                          }
                        : undefined
                    )
                  }
                />
              </div>
              {rules.url && typeof rules.url === 'object' && (
                <Input
                  value={rules.url.message || ''}
                  onChange={(e) =>
                    updateRule('url', {
                      enabled: true,
                      message: e.target.value,
                    })
                  }
                  placeholder="Error message"
                />
              )}
            </div>
          )}

          {/* Pattern (Regex) Validation */}
          {showPatternValidation && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Pattern (Regex)</Label>
                <Switch
                  checked={!!rules.pattern}
                  onCheckedChange={(checked) =>
                    updateRule(
                      'pattern',
                      checked
                        ? { value: '', message: 'Invalid format' }
                        : undefined
                    )
                  }
                />
              </div>
              {rules.pattern && (
                <div className="space-y-2">
                  <Input
                    value={String(rules.pattern.value || '')}
                    onChange={(e) =>
                      updateRule('pattern', {
                        ...rules.pattern,
                        value: e.target.value,
                      })
                    }
                    placeholder="e.g., ^[a-zA-Z0-9]+$"
                  />
                  <Input
                    value={rules.pattern.message || ''}
                    onChange={(e) =>
                      updateRule('pattern', {
                        ...rules.pattern,
                        message: e.target.value,
                      })
                    }
                    placeholder="Error message"
                  />
                </div>
              )}
            </div>
          )}

          {/* Not Same As Validation */}
          {otherFields.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Not Same As</Label>
                <Switch
                  checked={!!rules.notSameAs}
                  onCheckedChange={(checked) =>
                    updateRule(
                      'notSameAs',
                      checked
                        ? {
                            enabled: true,
                            field: otherFields[0]?.fieldId ?? '',
                            message:
                              'This value cannot be the same as another field',
                          }
                        : undefined
                    )
                  }
                />
              </div>
              {rules.notSameAs && (
                <div className="space-y-2">
                  <Select
                    value={rules.notSameAs.field || ''}
                    onValueChange={(value) =>
                      updateRule('notSameAs', {
                        ...rules.notSameAs,
                        field: value,
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select field to compare" />
                    </SelectTrigger>
                    <SelectContent>
                      {otherFields.map((f) => (
                        <SelectItem key={f.fieldId} value={f.fieldId}>
                          {f.label} ({f.fieldId})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    value={rules.notSameAs.message || ''}
                    onChange={(e) =>
                      updateRule('notSameAs', {
                        ...rules.notSameAs,
                        message: e.target.value,
                      })
                    }
                    placeholder="Error message"
                  />
                  <p className="txt-xs-regular text-text-tertiary">
                    This field cannot have the same value as the selected field
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Unique Values within Repeatable Module */}
          {isInRepeatableModule && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Unique values within entries</Label>
                <Switch
                  checked={!!rules.uniqueInRepeatable?.enabled}
                  onCheckedChange={(checked) =>
                    updateRule(
                      'uniqueInRepeatable',
                      checked
                        ? {
                            enabled: true,
                            message:
                              'This value must be unique across all entries',
                          }
                        : undefined
                    )
                  }
                />
              </div>
              {rules.uniqueInRepeatable?.enabled && (
                <div className="space-y-2">
                  <Input
                    value={rules.uniqueInRepeatable.message || ''}
                    onChange={(e) =>
                      updateRule('uniqueInRepeatable', {
                        ...rules.uniqueInRepeatable,
                        enabled: true,
                        message: e.target.value,
                      })
                    }
                    placeholder="Error message"
                  />
                  <p className="txt-xs-regular text-text-tertiary">
                    Each entry in this repeatable section must have a different
                    value for this field
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
